import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SpacesService } from '../../services/spaces.service';
import { PdfExportService } from '../../services/pdf-export.service';
import { MatomoService } from '../../services/matomo.service';
import { Space, CountryInfo } from '../../models/space.model';
import { SpaceConfigOptionsService } from '../../services/space-config-options.service';

@Component({
    selector: 'app-space-detail',
    imports: [CommonModule, FormsModule, RouterOutlet, TranslateModule],
    templateUrl: './space-detail.component.html',
    styleUrl: './space-detail.component.scss'
})
export class SpaceDetailComponent implements OnInit, OnDestroy {
  space: Space | null = null;
  spaceId = '';
  isExporting = false;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly spacesService: SpacesService,
    private readonly pdfExportService: PdfExportService,
    private readonly matomoService: MatomoService,
    private readonly spaceConfigOptions: SpaceConfigOptionsService
  ) {}

  ngOnInit(): void {
    this.spacesService.activeSpace$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(space => {
      this.space = space.id ? space : null;
    });

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.spaceId = params['spaceId'];
      this.loadSpace();
    });
  }

  ngOnDestroy(): void {
    this.spacesService.clearActiveSpace();
  }

  private loadSpace(): void {
    const found = this.spacesService.setActiveSpace(this.spaceId);
    if (!found) {
      this.router.navigate(['/spaces']);
    }
  }

  get countries() { return this.spaceConfigOptions.countries; }

  get currentCountryInfo(): CountryInfo {
    return this.spaceConfigOptions.getCountryInfo(this.space?.country || 'Germany');
  }

  resetAll(): void {
    if (this.space) {
      this.space.relevantIndustries = [];
      this.space.disciplines = [];
      this.space.relevantMaterials = [];
      this.space.teamSize = 0;
      this.space.averageHourlyRate = 0;
      this.space.projectsPerYear = 0;
      this.space.averageLicenceCost = 0;
      this.space.maturityLevel = 20;

      this.spacesService.update(this.space.id, {
        relevantIndustries: [],
        disciplines: [],
        relevantMaterials: [],
        selectedSolutions: [],
        teamSize: 0,
        averageHourlyRate: 0,
        projectsPerYear: 0,
        averageLicenceCost: 0,
        maturityLevel: 20
      });
      this.spacesService.updateSelectedSolutions([]);

      this.space = { ...this.space };
    }
  }

  async exportToPdf(): Promise<void> {
    if (this.isExporting || !this.space) return;
    
    this.isExporting = true;
    try {
      await this.pdfExportService.exportSpaceAsHtml(this.space, this.space.name);
      this.matomoService.trackActionPageView(
        this.matomoService.CATEGORY.export,
        this.matomoService.ACTION.pdfExportSuccess,
        'space_detail'
      );
    } catch (error) {
      this.matomoService.trackActionPageView(
        this.matomoService.CATEGORY.export,
        this.matomoService.ACTION.pdfExportFailed,
        'space_detail'
      );
      throw error;
    } finally {
      this.isExporting = false;
    }
  }
}
